import React, { useState } from "react";
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, IconButton, MenuItem,
  Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { useHd } from "../HelpdeskContext";
import ConditionBuilder from "./ConditionBuilder";
import { Section, useCrud } from "./common";
import { T, TYPE, formatDuration, labelSx, primaryButtonSx } from "../../user/complaints/shared";

const th = { "& th": { ...labelSx, bgcolor: T.surfaceHeader } };
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export const SlaEditor = () => {
  const ctx = useHd();
  const { rows, create, update, remove } = useCrud("/sla-policies");
  const hours = useCrud("/business-hours");
  const [edit, setEdit] = useState(null);
  const manage = ctx.can("sla.manage");
  if (!rows || !hours.rows) return <CircularProgress size={22} />;

  const blank = () => ({
    name: "", description: "", order: (rows.length + 1) * 10, conditions: { all: [], any: [] }, warningPct: 75, businessHoursId: hours.rows.find((h) => h.isDefault)?._id || "",
    targets: ctx.priorities.map((p) => ({ priorityId: p._id, firstResponseMins: 60, resolutionMins: 1440 })),
    escalation: { onWarning: { notifyAssignee: true, notifyTeamIds: [] }, onBreach: { notifyAssignee: true, notifyTeamIds: [], escalateToTeamId: null, raisePriority: false } }, isActive: true,
  });
  const open = (p) => {
    const targets = ctx.priorities.map((pr) => p.targets.find((t) => String(t.priorityId) === String(pr._id)) || { priorityId: pr._id, firstResponseMins: "", resolutionMins: "" });
    setEdit({ ...p, targets, businessHoursId: p.businessHoursId || "" });
  };
  const save = async () => {
    const body = {
      name: edit.name, description: edit.description, order: Number(edit.order), conditions: edit.conditions, warningPct: Number(edit.warningPct),
      businessHoursId: edit.businessHoursId || null, escalation: edit.escalation, isActive: edit.isActive,
      targets: edit.targets.filter((t) => t.firstResponseMins !== "" && t.resolutionMins !== "").map((t) => ({ ...t, firstResponseMins: Number(t.firstResponseMins), resolutionMins: Number(t.resolutionMins) })),
    };
    const ok = edit._id ? await update(edit._id, body) : await create(body);
    if (ok) setEdit(null);
  };
  const missing = ctx.priorities.filter((p) => p.isActive && !rows.some((r) => r.isActive && r.targets.some((t) => String(t.priorityId) === String(p._id))));
  const setT = (i, k, v) => setEdit((e) => ({ ...e, targets: e.targets.map((t, j) => (j === i ? { ...t, [k]: v } : t)) }));
  const esc = (phase, k, v) => setEdit((e) => ({ ...e, escalation: { ...e.escalation, [phase]: { ...e.escalation?.[phase], [k]: v } } }));

  return (
    <Box>
      {missing.length > 0 && <Alert severity="warning" sx={{ mb: 2 }}>No SLA target for: {missing.map((p) => p.name).join(", ")}. Tickets with these priorities have no SLA.</Alert>}
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Typography sx={{ fontSize: TYPE.small, color: T.inkSecondary }}>The first active policy (lowest order) whose conditions match a ticket applies.</Typography>
        {manage && <Button variant="contained" sx={primaryButtonSx} startIcon={<Plus size={15} />} onClick={() => setEdit(blank())}>Add policy</Button>}
      </Stack>
      <Table size="small">
        <TableHead><TableRow sx={th}><TableCell>Order</TableCell><TableCell>Policy</TableCell><TableCell>Targets (first response / resolution)</TableCell><TableCell>Business hours</TableCell><TableCell /></TableRow></TableHead>
        <TableBody>
          {[...rows].sort((a, b) => a.order - b.order).map((p) => (
            <TableRow key={p._id}>
              <TableCell>{p.order}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{p.name}{!p.isActive && " (inactive)"}<Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>{(p.conditions?.all?.length || 0) + (p.conditions?.any?.length || 0)} condition(s)</Typography></TableCell>
              <TableCell>{p.targets.map((t) => <Chip key={t.priorityId} size="small" sx={{ mr: 0.5, mb: 0.5 }} label={`${ctx.priorityById.get(String(t.priorityId))?.name || "?"}: ${formatDuration(t.firstResponseMins)} / ${formatDuration(t.resolutionMins)}`} />)}</TableCell>
              <TableCell>{hours.rows.find((h) => String(h._id) === String(p.businessHoursId))?.name || "24 x 7"}</TableCell>
              <TableCell align="right">{manage && <><IconButton size="small" onClick={() => open(p)}><Pencil size={15} /></IconButton><IconButton size="small" onClick={() => remove(p._id)}><Trash2 size={15} color="#B42318" /></IconButton></>}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {edit && (
        <Dialog open onClose={() => setEdit(null)} maxWidth="md" fullWidth>
          <DialogTitle>{edit._id ? "Edit SLA policy" : "New SLA policy"}</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={8}><TextField fullWidth label="Name" value={edit.name} onChange={(e) => setEdit((p) => ({ ...p, name: e.target.value }))} /></Grid>
              <Grid item xs={4}><TextField fullWidth type="number" label="Order" value={edit.order} onChange={(e) => setEdit((p) => ({ ...p, order: e.target.value }))} /></Grid>
              <Grid item xs={8}>
                <TextField select fullWidth label="Business hours" value={edit.businessHoursId} onChange={(e) => setEdit((p) => ({ ...p, businessHoursId: e.target.value }))}>
                  <MenuItem value="">24 x 7 (wall clock)</MenuItem>
                  {hours.rows.map((h) => <MenuItem key={h._id} value={h._id}>{h.name}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={4}><TextField fullWidth type="number" label="Warn at % of time used" value={edit.warningPct} onChange={(e) => setEdit((p) => ({ ...p, warningPct: e.target.value }))} /></Grid>
            </Grid>
            <Section title="Targets per priority (minutes)">
              {edit.targets.map((t, i) => (
                <Stack key={t.priorityId} direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                  <Typography sx={{ width: 140, fontSize: TYPE.small, fontWeight: 600 }}>{ctx.priorityById.get(String(t.priorityId))?.name}</Typography>
                  <TextField size="small" type="number" label="First response" value={t.firstResponseMins} onChange={(e) => setT(i, "firstResponseMins", e.target.value)} helperText={t.firstResponseMins ? formatDuration(Number(t.firstResponseMins)) : "no target"} />
                  <TextField size="small" type="number" label="Resolution" value={t.resolutionMins} onChange={(e) => setT(i, "resolutionMins", e.target.value)} helperText={t.resolutionMins ? formatDuration(Number(t.resolutionMins)) : "no target"} />
                </Stack>
              ))}
            </Section>
            <Section title="Applies when" description="Leave empty to apply to every ticket not matched by an earlier policy.">
              <ConditionBuilder value={edit.conditions} onChange={(conditions) => setEdit((p) => ({ ...p, conditions }))} />
            </Section>
            <Section title="Escalation">
              <FormControlLabel control={<Checkbox checked={edit.escalation?.onWarning?.notifyAssignee !== false} onChange={(e) => esc("onWarning", "notifyAssignee", e.target.checked)} />} label="On warning: notify the assigned agent" />
              <FormControlLabel control={<Checkbox checked={edit.escalation?.onBreach?.notifyAssignee !== false} onChange={(e) => esc("onBreach", "notifyAssignee", e.target.checked)} />} label="On breach: notify the assigned agent" />
              <FormControlLabel control={<Checkbox checked={Boolean(edit.escalation?.onBreach?.raisePriority)} onChange={(e) => esc("onBreach", "raisePriority", e.target.checked)} />} label="On breach: raise priority one level" />
              <TextField select fullWidth size="small" label="On breach: escalate to team" value={edit.escalation?.onBreach?.escalateToTeamId || ""} onChange={(e) => esc("onBreach", "escalateToTeamId", e.target.value || null)} sx={{ mt: 1 }}>
                <MenuItem value="">Don't move the ticket</MenuItem>
                {ctx.teams.map((t) => <MenuItem key={t._id} value={t._id}>{t.name}</MenuItem>)}
              </TextField>
            </Section>
            <FormControlLabel control={<Checkbox checked={edit.isActive} onChange={(e) => setEdit((p) => ({ ...p, isActive: e.target.checked }))} />} label="Active" />
          </DialogContent>
          <DialogActions><Button onClick={() => setEdit(null)}>Cancel</Button><Button variant="contained" sx={primaryButtonSx} onClick={save}>Save</Button></DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export const BusinessHoursEditor = () => {
  const ctx = useHd();
  const { rows, create, update, remove } = useCrud("/business-hours");
  const [edit, setEdit] = useState(null);
  if (!rows) return <CircularProgress size={22} />;
  const manage = ctx.can("sla.manage");
  const dayRow = (d) => edit.weekly.find((w) => w.day === d);
  const setDay = (d, windows) => setEdit((e) => ({ ...e, weekly: [...e.weekly.filter((w) => w.day !== d), ...(windows ? [{ day: d, windows }] : [])].sort((a, b) => a.day - b.day) }));
  const save = async () => {
    const body = { name: edit.name, timezone: edit.timezone, alwaysOpen: edit.alwaysOpen, weekly: edit.weekly, holidays: edit.holidays.filter((h) => h.date), isDefault: edit.isDefault };
    const ok = edit._id ? await update(edit._id, body) : await create(body);
    if (ok) setEdit(null);
  };
  return (
    <Box>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1.5 }}>
        {manage && <Button variant="contained" sx={primaryButtonSx} startIcon={<Plus size={15} />} onClick={() => setEdit({ name: "", timezone: "Asia/Kolkata", alwaysOpen: false, weekly: [1, 2, 3, 4, 5].map((day) => ({ day, windows: [{ start: "09:00", end: "18:00" }] })), holidays: [], isDefault: false })}>Add calendar</Button>}
      </Stack>
      <Table size="small">
        <TableHead><TableRow sx={th}><TableCell>Name</TableCell><TableCell>Timezone</TableCell><TableCell>Schedule</TableCell><TableCell>Holidays</TableCell><TableCell /></TableRow></TableHead>
        <TableBody>
          {rows.map((h) => (
            <TableRow key={h._id}>
              <TableCell sx={{ fontWeight: 600 }}>{h.name}</TableCell>
              <TableCell>{h.timezone}</TableCell>
              <TableCell>{h.alwaysOpen ? "24 x 7" : h.weekly.map((w) => `${DAYS[w.day]} ${w.windows.map((x) => `${x.start}-${x.end}`).join(",")}`).join(" · ")}</TableCell>
              <TableCell>{h.holidays.length}</TableCell>
              <TableCell align="right">{manage && <><IconButton size="small" onClick={() => setEdit({ ...h })}><Pencil size={15} /></IconButton><IconButton size="small" onClick={() => remove(h._id)}><Trash2 size={15} color="#B42318" /></IconButton></>}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {edit && (
        <Dialog open onClose={() => setEdit(null)} maxWidth="sm" fullWidth>
          <DialogTitle>Business hours</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={7}><TextField fullWidth label="Name" value={edit.name} onChange={(e) => setEdit((p) => ({ ...p, name: e.target.value }))} /></Grid>
              <Grid item xs={5}><TextField fullWidth label="Timezone (IANA)" value={edit.timezone} onChange={(e) => setEdit((p) => ({ ...p, timezone: e.target.value }))} helperText="e.g. Asia/Kolkata, Asia/Dubai" /></Grid>
              <Grid item xs={12}><FormControlLabel control={<Checkbox checked={edit.alwaysOpen} onChange={(e) => setEdit((p) => ({ ...p, alwaysOpen: e.target.checked }))} />} label="Open 24 x 7" /></Grid>
            </Grid>
            {!edit.alwaysOpen && DAYS.map((label, d) => {
              const row = dayRow(d);
              return (
                <Stack key={d} direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <FormControlLabel sx={{ width: 90 }} control={<Checkbox size="small" checked={Boolean(row)} onChange={(e) => setDay(d, e.target.checked ? [{ start: "09:00", end: "18:00" }] : null)} />} label={label} />
                  {row && row.windows.map((w, i) => (
                    <Stack key={i} direction="row" spacing={0.5}>
                      <TextField size="small" type="time" value={w.start} onChange={(e) => setDay(d, row.windows.map((x, j) => (j === i ? { ...x, start: e.target.value } : x)))} />
                      <TextField size="small" type="time" value={w.end} onChange={(e) => setDay(d, row.windows.map((x, j) => (j === i ? { ...x, end: e.target.value } : x)))} />
                    </Stack>
                  ))}
                  {row && <Button size="small" onClick={() => setDay(d, [...row.windows, { start: "14:00", end: "18:00" }])}>+ split</Button>}
                </Stack>
              );
            })}
            <Section title="Holidays">
              {edit.holidays.map((h, i) => (
                <Stack key={i} direction="row" spacing={1} sx={{ mb: 1 }}>
                  <TextField size="small" type="date" value={h.date} onChange={(e) => setEdit((p) => ({ ...p, holidays: p.holidays.map((x, j) => (j === i ? { ...x, date: e.target.value } : x)) }))} />
                  <TextField size="small" placeholder="Name" value={h.name} onChange={(e) => setEdit((p) => ({ ...p, holidays: p.holidays.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)) }))} />
                  <IconButton size="small" onClick={() => setEdit((p) => ({ ...p, holidays: p.holidays.filter((_, j) => j !== i) }))}><Trash2 size={14} /></IconButton>
                </Stack>
              ))}
              <Button size="small" onClick={() => setEdit((p) => ({ ...p, holidays: [...p.holidays, { date: "", name: "" }] }))}>Add holiday</Button>
            </Section>
          </DialogContent>
          <DialogActions><Button onClick={() => setEdit(null)}>Cancel</Button><Button variant="contained" sx={primaryButtonSx} onClick={save}>Save</Button></DialogActions>
        </Dialog>
      )}
    </Box>
  );
};
